import React, { useState } from 'react';
import { clsx } from 'clsx';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

export type InputSize = 'sm' | 'md' | 'lg';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  helperText?: string;
  error?: string;
  size?: InputSize;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  floatingLabel?: boolean;
}

const sizeStyles: Record<InputSize, string> = {
  sm: 'px-3 py-2 text-sm',
  md: 'px-4 py-2.5 text-sm',
  lg: 'px-4 py-3 text-base',
};

const iconSizeStyles: Record<InputSize, string> = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-5 w-5',
};

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helperText,
      error,
      size = 'md',
      leftIcon,
      rightIcon,
      floatingLabel = false,
      disabled,
      className,
      type,
      id,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [hasValue, setHasValue] = useState(!!props.value || !!props.defaultValue);

    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    const isPassword = type === 'password';
    const inputType = isPassword && showPassword ? 'text' : type;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setHasValue(!!e.target.value);
      props.onChange?.(e);
    };

    const baseInputStyles = clsx(
      'w-full rounded-lg border bg-white text-neutral-900 placeholder-neutral-400',
      'transition-all duration-200',
      'focus:outline-none focus:ring-2 focus:ring-offset-0',
      sizeStyles[size],
      leftIcon && 'pl-10',
      (rightIcon || isPassword) && 'pr-10',
      error
        ? 'border-error-500 focus:border-error-500 focus:ring-error-500/20'
        : 'border-neutral-300 hover:border-neutral-400 focus:border-primary-500 focus:ring-primary-500/20',
      disabled && 'bg-neutral-100 cursor-not-allowed opacity-60',
      className
    );

    return (
      <div className="w-full">
        {/* Standard Label */}
        {label && !floatingLabel && (
          <label htmlFor={inputId} className="block text-sm font-medium text-neutral-700 mb-1.5">
            {label}
          </label>
        )}

        {/* Input Container */}
        <div className="relative">
          {/* Left Icon */}
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none">
              <span className={iconSizeStyles[size]}>{leftIcon}</span>
            </div>
          )}

          {/* Input */}
          <input
            ref={ref}
            id={inputId}
            type={inputType}
            disabled={disabled}
            className={baseInputStyles}
            onFocus={(e) => {
              setIsFocused(true);
              props.onFocus?.(e);
            }}
            onBlur={(e) => {
              setIsFocused(false);
              props.onBlur?.(e);
            }}
            onChange={handleChange}
            {...props}
          />

          {/* Floating Label */}
          {label && floatingLabel && (
            <label
              htmlFor={inputId}
              className={clsx(
                'absolute left-3 transition-all duration-200 pointer-events-none',
                isFocused || hasValue
                  ? '-top-2.5 text-xs bg-white px-1 text-primary-600 font-medium'
                  : 'top-1/2 -translate-y-1/2 text-neutral-400'
              )}
            >
              {label}
            </label>
          )}

          {/* Right Icon or Password Toggle */}
          {(rightIcon || isPassword) && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {isPassword ? (
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-neutral-400 hover:text-neutral-600 transition-colors"
                >
                  {showPassword ? (
                    <EyeSlashIcon className={iconSizeStyles[size]} />
                  ) : (
                    <EyeIcon className={iconSizeStyles[size]} />
                  )}
                </button>
              ) : (
                <span className={clsx(iconSizeStyles[size], 'text-neutral-400')}>{rightIcon}</span>
              )}
            </div>
          )}
        </div>

        {/* Helper Text or Error */}
        {(helperText || error) && (
          <p className={clsx('mt-1.5 text-sm', error ? 'text-error-600' : 'text-neutral-500')}>
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;

